import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Button, Dropdown, Input, SidePanel } from '../ui';

interface ManualTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const FORM_ID = 'manual-transaction-form';

const CURRENCY_OPTIONS = [
  { value: 'usd', label: 'USD' },
  { value: 'eur', label: 'EUR' },
  { value: 'gbp', label: 'GBP' },
  { value: 'cad', label: 'CAD' },
];

const TYPE_OPTIONS = [
  { value: 'manual_entry', label: 'Manual Entry' },
  { value: 'check_payment', label: 'Check Payment' },
  { value: 'wire_transfer', label: 'Wire Transfer' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' },
];

const initialForm = () => ({
  description: '',
  amount: '',
  currency: 'usd',
  transaction_type: 'manual_entry',
  status: 'paid',
  customer_name: '',
  customer_email: '',
  created_at: new Date().toISOString().split('T')[0],
});

export default function ManualTransactionModal({ isOpen, onClose, onSuccess }: ManualTransactionModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState(initialForm());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const amountInCents = Math.round(parseFloat(formData.amount) * 100);

      if (isNaN(amountInCents)) {
        throw new Error('Invalid amount');
      }

      const transactionId = `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      const { error: insertError } = await supabase
        .from('stripe_transactions')
        .insert({
          stripe_transaction_id: transactionId,
          amount: amountInCents,
          currency: formData.currency,
          status: formData.status,
          transaction_type: formData.transaction_type,
          description: formData.description,
          customer_name: formData.customer_name || null,
          customer_email: formData.customer_email || null,
          created_at: new Date(formData.created_at).toISOString(),
          is_manual: true,
          metadata: {},
        });

      if (insertError) throw insertError;

      onSuccess();
      onClose();
      setFormData(initialForm());
    } catch (err) {
      console.error('Error creating manual transaction:', err);
      setError(err instanceof Error ? err.message : 'Failed to create transaction');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SidePanel
      open={isOpen}
      onClose={onClose}
      width={420}
      eyebrow="Manual Entry"
      title="Add Transaction"
      footer={
        <>
          <Button icon="plus" type="submit" form={FORM_ID} disabled={isLoading}>
            {isLoading ? 'Adding...' : 'Add Transaction'}
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={handleSubmit}>
        <div className="ck-stagger" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {error && (
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--status-down-soft)',
                border: '1px solid rgba(229, 72, 77, 0.25)',
                color: 'var(--danger-400)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {error}
            </div>
          )}

          <Input
            label="Description"
            icon="pencil"
            required
            placeholder="What was it for..."
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Input
              label="Amount"
              icon="circle-dollar-sign"
              type="number"
              required
              step="0.01"
              min="0"
              placeholder="0.00"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            />
            <Dropdown
              label="Currency"
              options={CURRENCY_OPTIONS}
              value={formData.currency}
              onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <div className="t-eyebrow" style={{ marginBottom: '7px' }}>
                Date
              </div>
              <input
                type="date"
                className="ck-date-input"
                required
                value={formData.created_at}
                onChange={(e) => setFormData({ ...formData, created_at: e.target.value })}
              />
            </div>
            <Dropdown
              label="Type"
              options={TYPE_OPTIONS}
              value={formData.transaction_type}
              onChange={(e) => setFormData({ ...formData, transaction_type: e.target.value })}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Input
              label="Customer (Optional)"
              icon="building-2"
              placeholder="—"
              value={formData.customer_name}
              onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
            />
            <Input
              label="Email (Optional)"
              icon="mail"
              type="email"
              placeholder="—"
              value={formData.customer_email}
              onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
            />
          </div>

          <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
            Manual entries sit alongside Stripe-synced transactions and roll into the P&L.
          </p>
        </div>
      </form>
    </SidePanel>
  );
}
